import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { ExpandableNotification } from 'src/components/ExpandableNotification';
import { deviceHelpers } from 'src/utils/deviceHelpers';
import { fleetUserHelpers } from './fleetUserHelpers';

export function DeviceVerificationNotification({
  device,
  darken = false,
}: {
  device: FleetDevice;
  darken?: boolean;
}) {
  const verificationStatus =
    fleetUserHelpers.getDeviceVerificationStatus(device);

  const isLegacyDevice = deviceHelpers.isLegacySecurityDevice(device);

  switch (verificationStatus) {
    case 'Verified':
      return null;

    case 'Confirmed': {
      return (
        <ExpandableNotification
          data-cy="device-verification-notification"
          aria-label="Verification confirmed"
          level="success"
          title="Verification confirmed"
          className={darken ? 'bg-paper-4' : undefined}
        >
          This device is operating with standard software security. Hardware
          verification is exclusive to reMarkable Paper Pro and newer models.
        </ExpandableNotification>
      );
    }

    case 'Not verified': {
      if (isLegacyDevice) {
        return (
          <ExpandableNotification
            data-cy="device-verification-notification"
            aria-label="Verification failed"
            level="error"
            title="Verification failed"
            className={darken ? 'bg-paper-4' : undefined}
          >
            The system is unable to verify the authenticity of this device.
            Ensure the device is connected to Wi-Fi and running the latest
            software. If the issue persists, try restarting the device.
          </ExpandableNotification>
        );
      }

      return (
        <ExpandableNotification
          data-cy="device-verification-notification"
          aria-label="Verification failed"
          level="error"
          title="Verification failed"
          className={darken ? 'bg-paper-4' : undefined}
        >
          The system can&apos;t verify the authenticity of this device. Check
          that Developer mode is turned off, the device is connected to Wi-Fi,
          and that the software is up to date. If the issue persists, try
          restarting it.
        </ExpandableNotification>
      );
    }

    case 'Unavailable':
      return (
        <ExpandableNotification
          data-cy="device-verification-notification"
          aria-label="Status unavilable"
          level="warning"
          title="Verification status unavailable"
          className={darken ? 'bg-paper-4' : undefined}
        >
          The system can&apos;t currently determine the verification status of
          this device. Check that the device is connected to Wi-Fi and running
          the latest software, or try restarting it.
        </ExpandableNotification>
      );
    default: {
      return null;
    }
  }
}
